import React from 'react';
import { User, MapPin, Copy } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { ShippingAddress } from '../../types/order';

interface CustomerShippingInfoProps {
  shippingAddress: ShippingAddress;
  copyToClipboard: (text: string) => void;
}

const CustomerShippingInfo: React.FC<CustomerShippingInfoProps> = ({
  shippingAddress,
  copyToClipboard
}) => {
  return (
    <div className="bg-white border-b border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <User className="w-5 h-5 mr-2" />
          Customer & Shipping Information
        </h2>
      </div>
      <div className="px-6 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer Details */}
          <div>
            <div className="space-y-3">
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Name</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-sm font-medium text-gray-900">{shippingAddress.name}</p>
                  <button
                    onClick={() => copyToClipboard(shippingAddress.name)}
                    className="p-1 text-gray-400 hover:text-indigo-600 transition-all"
                    title="Copy Name"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Mobile</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-sm text-gray-900 font-medium">{shippingAddress.mobile}</p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => copyToClipboard(shippingAddress.mobile)}
                      className="p-1 text-gray-400 hover:text-indigo-600 transition-all"
                      title="Copy Mobile"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
              {shippingAddress.alternateMobile && (
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Alternate Mobile</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-sm text-gray-900 font-medium">{shippingAddress.alternateMobile}</p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => copyToClipboard(shippingAddress.alternateMobile!)}
                        className="p-1 text-gray-400 hover:text-indigo-600 transition-all"
                        title="Copy Alternate Mobile"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => {
                          const cleaned = shippingAddress.alternateMobile!.replace(/\D/g, '');
                          const whatsappNumber = cleaned.length === 10 ? `91${cleaned}` : cleaned;
                          window.open(`https://wa.me/${whatsappNumber}`, '_blank');
                        }}
                        className="inline-flex items-center p-1 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                        title="Chat on WhatsApp"
                      >
                        <FaWhatsapp className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Shipping Address */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900 flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                Shipping Address
              </h3>
              <button
                onClick={() => {
                  const addr = shippingAddress;
                  const fullAddress = `${addr.name}\n${addr.mobile}\n${addr.line1}${addr.line2 ? '\n' + addr.line2 : ''}${addr.line3 ? '\n' + addr.line3 : ''}\n${addr.district}, ${addr.state}\n${addr.pincode}`;
                  copyToClipboard(fullAddress);
                }}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy Address
              </button>
            </div>
            <div className="space-y-1 text-sm">
              <p className="text-gray-600">{shippingAddress.line1}</p>
              {shippingAddress.line2 && (
                <p className="text-gray-600">{shippingAddress.line2}</p>
              )}
              {shippingAddress.line3 && (
                <p className="text-gray-600">{shippingAddress.line3}</p>
              )}
              <p className="text-gray-600">{shippingAddress.district}, {shippingAddress.state}</p>
              <p className="text-gray-600">{shippingAddress.pincode}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerShippingInfo;
